'use client'
// [Other Buying Approaches - WRH-9]
import { faLongArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { FormattedMessage, ButtonAsA } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import Image from '@/components/Image'
import type {
  ExploreBuyingCard,
  ExploreBuyingData,
} from '@/helpers/cms/contentTypes'

const StyledSection = styled.section`
  background-color: ${designColors.neutral.white};
  display: flex;
  padding: 3rem;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  width: 100%;
  margin: 0 auto;

  @media screen and (max-width: 769px) {
    padding: 2.5rem 1.5rem;
  }
`

const Content = styled.div`
  width: 100%;
  max-width: var(--maxPageWidth);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3rem;
`

const Title = styled.h2`
  ${Typography.Title2};
  color: ${designColors.neutral.veryDarkGray};

  @media screen and (max-width: 769px) {
    font-size: 1.5rem;
  }
`

const CardContainer = styled.div`
  align-items: center;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  justify-content: center;
  width: 100%;

  @media ${Breakpoints.min.medium} {
    align-items: stretch;
    flex-direction: row;
    gap: 3rem;
  }
`

const Card = styled.div<{ $bgColor?: string }>`
  border-radius: 1rem;
  background-color: ${({ $bgColor }) => $bgColor || designColors.neutral.white};
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1.5rem;
  justify-content: space-between;
  padding: 2rem;

  @media screen and (max-width: 1200px) {
    width: 100%;
  }

  @media screen and (max-width: 769px) {
    padding: 1.5rem;
  }
`

const StyledImage = styled(Image)`
  object-fit: contain;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const CardTitle = styled.h3`
  ${Typography.Title3};
  color: ${designColors.neutral.veryDarkGray};
`

const CardText = styled.p`
  ${Typography.Body1};
  color: ${designColors.neutral.veryDarkGray};
  max-width: 22.5rem;
`

const StyledButton = styled(ButtonAsA)`
  width: fit-content;
  max-width: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  white-space: nowrap;

  background-color: ${designColors.neutral.white};
  color: ${designColors.primary.default};

  &&:hover {
    color: ${designColors.neutral.white};
    background-color: ${designColors.primary.default};
  }
`
/**
 * Card fills as names rather than hex, so the design-system colour stays a token. Cream is the
 * one-off the spot-sales card has always used; it has no token.
 */
const CARD_BACKGROUNDS: Record<ExploreBuyingCard['background'], string> = {
  lightBlue: designColors.primary.backgroundLightBlue,
  cream: '#FFF9E5',
  white: designColors.neutral.white,
}

export default function ExploreBuying({
  data,
}: {
  /** CMS copy when the marketplace-content flag is on and this section is filled in. */
  data?: ExploreBuyingData | null
}) {
  return (
    <StyledSection data-testid="explore-buying">
      <Content>
        {/*
          Blank falls back to the shipped copy rather than hiding the heading, matching Best
          Place and Featured Auctions. Hiding it also dropped one 3rem gap from this column, so
          clearing a field silently changed the section's height as well as its content.

          One tree for both sources, so the section, its heading and its gaps are described once.
          Only the cards themselves differ — the bundled pair carry translated copy and committed
          artwork, which no CMS document reproduces.
        */}
        <Title>
          {data?.title ?? (
            <FormattedMessage id="Marketing.exploreBuying.title" />
          )}
        </Title>
        <CardContainer>
          {data?.cards.length
            ? data.cards.map((card, cardIdx) => (
                /*
                  `id` first — two cards can point at the same URL with different copy. The index
                  behind it rather than the URL: Payload writes an id for every row, but a document
                  migrated from elsewhere might not carry one, and two such cards sharing a URL
                  would collide on the key and drop one.
                */
                <Card
                  key={card.id ?? `card-${cardIdx}`}
                  $bgColor={CARD_BACKGROUNDS[card.background]}
                >
                  {card.image ? (
                    <StyledImage
                      id={card.title}
                      src={card.image}
                      alt=""
                      width={148}
                      height={140}
                    />
                  ) : null}

                  <CardContent>
                    <CardTitle>{card.title}</CardTitle>
                    <CardText>{card.body}</CardText>
                  </CardContent>

                  <StyledButton
                    appearance="primary"
                    size="large"
                    href={card.url}
                    target={card.openInNewTab ? '_blank' : undefined}
                    rel={card.openInNewTab ? 'noopener' : undefined}
                  >
                    {data.ctaLabel ?? (
                      <FormattedMessage id="Marketing.exploreBuying.startShopping" />
                    )}{' '}
                    <FontAwesomeIcon icon={faLongArrowRight} />
                  </StyledButton>
                </Card>
              ))
            : bundledCards}
        </CardContainer>
      </Content>
    </StyledSection>
  )
}

/**
 * The two cards this repo ships, rendered when the CMS section is empty.
 *
 * Kept whole rather than expressed as data: their copy is translated and their artwork is
 * committed here, neither of which a CMS card carries.
 */
const bundledCards = (
  <>
    <Card $bgColor={designColors.primary.backgroundLightBlue}>
      <StyledImage
        id="Contracts"
        src="/home-portal/images/contracts.png"
        alt="B-Stock Solutions, LLC."
        width={148}
        height={140}
      />

      <CardContent>
        <CardTitle>
          <FormattedMessage id="Marketing.exploreBuying.contracts.title" />
        </CardTitle>
        <CardText>
          <FormattedMessage id="Marketing.exploreBuying.contracts.description" />
        </CardText>
      </CardContent>

      <StyledButton
        appearance="primary"
        size="large"
        href="/contracts"
        target="_blank"
        rel="noopener"
      >
        <FormattedMessage id="Marketing.exploreBuying.startShopping" />{' '}
        <FontAwesomeIcon icon={faLongArrowRight} />
      </StyledButton>
    </Card>
    <Card $bgColor="#FFF9E5">
      <StyledImage
        id="Spot Sales"
        src="/home-portal/images/flash_sales.png"
        alt="B-Stock Solutions, LLC."
        width={148}
        height={140}
      />

      <CardContent>
        <CardTitle>
          <FormattedMessage id="Marketing.exploreBuying.spotSales.title" />
        </CardTitle>
        <CardText>
          <FormattedMessage id="Marketing.exploreBuying.spotSales.description" />
        </CardText>
      </CardContent>

      <StyledButton
        appearance="primary"
        size="large"
        href="/all-auctions/"
        target="_blank"
        rel="noopener"
      >
        <FormattedMessage id="Marketing.exploreBuying.startShopping" />{' '}
        <FontAwesomeIcon icon={faLongArrowRight} />
      </StyledButton>
    </Card>
  </>
)
